/**
 * Copyright (c) 2017 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 */
import { IntMap, SortedArray } from '../../../../mol-data/int';
import { sortArray } from '../../../../mol-data/util';
import { StructureSymmetry } from '../symmetry';
import { Structure } from '../structure';
import { UniqueArray } from '../../../../mol-data/generic';

var StructureUniqueSubsetBuilder =
/** @class */
function () {
  function StructureUniqueSubsetBuilder(parent) {
    this.parent = parent;
    this.ids = [];
    this.unitMap = IntMap.Mutable();
    this.parentId = -1;
    this.currentUnit = UniqueArray.create();
    this.elementCount = 0;
  }

  StructureUniqueSubsetBuilder.prototype.addToUnit = function (parentId, e) {
    var unit = this.unitMap.get(parentId);

    if (!!unit) {
      if (UniqueArray.add(unit, e, e)) this.elementCount++;
    } else {
      var arr = UniqueArray.create();
      UniqueArray.add(arr, e, e);
      this.unitMap.set(parentId, arr);
      this.ids[this.ids.length] = parentId;
      this.elementCount++;
    }
  };

  StructureUniqueSubsetBuilder.prototype.has = function (parentId, e) {
    var unit = this.unitMap.get(parentId);
    if (!unit) return false;
    return UniqueArray.has(unit, e);
  };

  StructureUniqueSubsetBuilder.prototype.beginUnit = function (parentId) {
    this.parentId = parentId;

    if (this.unitMap.has(parentId)) {
      this.currentUnit = this.unitMap.get(parentId);
    } else {
      this.currentUnit = this.currentUnit.array.length > 0 ? UniqueArray.create() : this.currentUnit;
    }
  };

  StructureUniqueSubsetBuilder.prototype.addElement = function (e) {
    if (UniqueArray.add(this.currentUnit, e, e)) this.elementCount++;
  };

  StructureUniqueSubsetBuilder.prototype.commitUnit = function () {
    if (this.currentUnit.array.length === 0 || this.unitMap.has(this.parentId)) return;
    this.ids[this.ids.length] = this.parentId;
    this.unitMap.set(this.parentId, this.currentUnit);
    this.parentId = -1;
  };

  StructureUniqueSubsetBuilder.prototype.getStructure = function () {
    if (this.isEmpty) return Structure.Empty;
    var newUnits = [];
    sortArray(this.ids);
    var symmGroups = StructureSymmetry.UnitEquivalenceBuilder();

    for (var i = 0, _i = this.ids.length; i < _i; i++) {
      var id = this.ids[i];
      var parent_1 = this.parent.unitMap.get(id);
      var unit = this.unitMap.get(id).array;
      var l = unit.length; // if the length is the same, just copy the old unit.

      if (unit.length === parent_1.elements.length) {
        newUnits[newUnits.length] = parent_1;
        symmGroups.add(parent_1.id, parent_1);
        continue;
      }

      if (l > 1) sortArray(unit);
      var child = parent_1.getChild(SortedArray.ofSortedArray(unit));
      var pivot = symmGroups.add(child.id, child);
      if (child !== pivot) child = pivot.applyOperator(child.id, child.conformation.operator, true);
      newUnits[newUnits.length] = child;
    }

    return Structure.create(newUnits, {
      parent: this.parent
    });
  };

  Object.defineProperty(StructureUniqueSubsetBuilder.prototype, "isEmpty", {
    get: function () {
      return this.elementCount === 0;
    },
    enumerable: false,
    configurable: true
  });
  return StructureUniqueSubsetBuilder;
}();

export { StructureUniqueSubsetBuilder };